import { TEXT_FIELD_MAX_LENGTH_FOR_ENQUIRIES } from '../../src/components/common/form/clubcentric-max-lengths';

export enum WidgetType {
  STRING = 'string',
  TEXT = 'text',
  MARKDOWN = 'markdown',
  NUMBER = 'number',
  BOOLEAN = 'boolean',
  DATETIME = 'datetime',
  IMAGE = 'hosted-image',
  IMAGE_WITH_CROP = 'hosted-image-with-crop',
  BLAZE_GAME_SELECT = 'blaze-game-select',
  SELECT = 'select',
  LIST = 'list',
  OBJECT = 'object',
  HIDDEN = 'hidden',
  PAGE_LANGUAGE = 'page-language',
  URL_SUFFIX = 'url-suffix',
  CLUB_SELECT = 'club-select',
  CONTENT_ORDER = 'content-order',
  FILE = 'file',
  RELATION = 'relation',
  FUTURE_DATE_TIME = 'future-date-time',
}

export interface WidgetBaseInterface {
  name: string;
  label: string;
  widget: WidgetType;
  hint?: string;
  i18n?: boolean | 'duplicate';

  [others: string]: any;
}

export interface SelectWidgetOption {
  label: string | null;
  value: string | number;
}

export type StringWidgetOptions = {
  name: string;
  label: string;
  hint?: string;
  required?: boolean;
  defaultValue?: string;
  pattern?: [string, string];
  i18n?: boolean | 'duplicate';
};

export const stringWidget = ({
  name,
  label,
  hint = '',
  required = false,
  defaultValue = '',
  i18n,
  pattern,
}: StringWidgetOptions): WidgetBaseInterface => ({
  name: name,
  label: label,
  hint: hint,
  widget: WidgetType.STRING,
  required,
  default: defaultValue,
  i18n,
  pattern,
});

export const pageCodeWidget = (): WidgetBaseInterface =>
  stringWidget({
    name: 'pageCode',
    label: 'Page Code',
    hint: 'An identifier that should be used for the same page across different languages and domains. This is used for the language switcher to navigate to the correct page and for SEO to link similar pages together.',
  });

export const requiredStringWidget = (
  options: Omit<StringWidgetOptions, 'required'>
): WidgetBaseInterface =>
  stringWidget({
    ...options,
    required: true,
  });

export const stringLinkWidget = (
  options: Omit<StringWidgetOptions, 'pattern'>
): WidgetBaseInterface =>
  stringWidget({
    ...options,
    pattern: ['^[^\\s]*$', 'Please remove any whitespace from the link'],
  });

type TextWidgetOptions = {
  name: string;
  label: string;
  required?: Boolean;
  hint?: string;
  maxLength?: number;
};

export const textWidget = ({
  name,
  label,
  required = true,
  hint = '',
  maxLength = TEXT_FIELD_MAX_LENGTH_FOR_ENQUIRIES,
}: TextWidgetOptions): WidgetBaseInterface => ({
  name,
  label,
  widget: WidgetType.TEXT,
  hint,
  required,
  maxLength,
});

type MarkdownWidgetOptions = {
  name: string;
  label: string;
  required?: Boolean;
  hint?: string;
  defaultValue?: string;
  pattern?: Array<string>;
  i18n?: boolean;
  buttons?: Array<string>;
};

export const markdownWidget = ({
  name,
  label,
  required = true,
  hint = '',
  defaultValue = '',
  pattern,
  buttons = [
    'bold',
    'italic',
    'link',
    'heading-one',
    'heading-two',
    'quote',
    'bulleted-list',
    'numbered-list',
  ],
  i18n,
}: MarkdownWidgetOptions): WidgetBaseInterface => ({
  name,
  label,
  widget: WidgetType.MARKDOWN,
  hint: hint,
  buttons,
  required,
  default: defaultValue,
  pattern,
  i18n,
});

type NumberWidgetOptions = {
  name: string;
  label: string;
  hint?: string;
  required?: boolean;
  min?: number;
  max?: number;
  defaultValue?: number;
};

export const numberWidget = ({
  name,
  label,
  hint = '',
  required = true,
  min = undefined,
  max = undefined,
  defaultValue,
}: NumberWidgetOptions): WidgetBaseInterface => ({
  name: name,
  label: label,
  widget: WidgetType.NUMBER,
  valuetype: 'int',
  hint,
  required,
  min,
  max,
  default: defaultValue,
});

type StringFloatWidgetOptions = {
  name: string;
  label: string;
  hint?: string;
  required?: boolean;
};
// There is a bug in the Netlify CMS number widget that doesn't allow floating point numbers to
// be entered, even with "valuetype: 'float'" (https://github.com/netlify/netlify-cms/issues/4808)
// so we instead need to enter the value as a string, validate the format with a regex, then parse
// to a number where required.
export const stringFloatWidget = ({
  name,
  label,
  hint = '',
  required = true,
}: StringFloatWidgetOptions): WidgetBaseInterface => ({
  name: name,
  label: label,
  hint: hint,
  widget: WidgetType.STRING,
  required: required,
  default: '',
  pattern: ['^-?\\d*(\\.\\d+)?$', 'Invalid number format'],
});

type BooleanWidgetOptions = {
  name: string;
  label: string;
  hint?: string;
  defaultValue?: boolean;
  hidden?: boolean;
  i18n?: boolean;
};

export const booleanWidget = ({
  name,
  label,
  hint = '',
  defaultValue = false,
  hidden = false,
  i18n = false,
}: BooleanWidgetOptions): WidgetBaseInterface => ({
  name: name,
  label: label,
  hint: hint,
  widget: hidden ? WidgetType.HIDDEN : WidgetType.BOOLEAN,
  default: defaultValue,
  required: false,
  i18n,
});

type DateTimeWidgetOptions = {
  name: string;
  label: string;
};

export const dateTimeWidget = ({ name, label }: DateTimeWidgetOptions): WidgetBaseInterface => ({
  name: name,
  label: label,
  widget: WidgetType.DATETIME,
  format: 'YYYY-MM-DDTHH:mm:ss',
});

type DateWidgetOptions = {
  name: string;
  label: string;
  hint?: string;
  required?: boolean;
  format?: string;
};

export const dateWidget = ({
  name,
  label,
  hint = '',
  required = false,
  format = 'YYYY-MM-DD',
}: DateWidgetOptions): WidgetBaseInterface => ({
  name: name,
  label: label,
  widget: WidgetType.DATETIME,
  required: required,
  default: '',
  dateFormat: format,
  hint,
});

type RequiredDateWidgetOptions = {
  name: string;
  label: string;
  hint?: string;
};

export const requiredDateWidget = ({
  name,
  label,
  hint = '',
}: RequiredDateWidgetOptions): WidgetBaseInterface =>
  dateWidget({ name, label, hint, required: true });

type ImageWidgetOptions = {
  name?: string;
  label?: string;
  required?: boolean;
  defaultValue?: string;
};

export const imageWidget = ({
  name,
  label,
  required,
  defaultValue = '',
}: ImageWidgetOptions): WidgetBaseInterface => ({
  name: name || 'image',
  label: label || 'Image',
  widget: WidgetType.IMAGE,
  required: !!required,
  default: defaultValue,
});

export type ImageWithCropWidgetOptions = {
  name?: string;
  label?: string;
  defaultUrl?: string;
  required?: boolean;
  mobileCropRequired?: boolean;
  mobileImageHeightPx: number;
  mobileImageWidthPx: number;
  includeDesktopCrop?: boolean;
  desktopImageHeightPx?: number;
  desktopImageWidthPx?: number;
};

export const imageWithCropWidget = ({
  name,
  label,
  defaultUrl = '',
  ...rest
}: ImageWithCropWidgetOptions): WidgetBaseInterface => ({
  name: name || 'image',
  label: label || 'Image',
  widget: WidgetType.IMAGE_WITH_CROP,
  default: {
    imagePath: defaultUrl,
  },
  ...rest,
});

type BlazeGameSelectWidgetOptions = {
  name?: string;
  label?: string;
};

export const blazeGameSelectWidget = ({
  name = 'blazeGame',
  label = 'Select Blaze Game',
}: BlazeGameSelectWidgetOptions = {}) => ({
  name: name || 'blazeGame',
  label: label || 'Select Blaze Game',
  widget: WidgetType.BLAZE_GAME_SELECT,
});

type DateTimeWithValidationWidgetOptions = {
  name?: string;
  label?: string;
};

export const dateTimeWithValidationWidget = ({
  name = 'endDateAndTime',
  label = 'Select Date and Time',
}: DateTimeWithValidationWidgetOptions = {}) => ({
  name: name || 'endDateAndTime',
  label: label || 'Select Date and Time',
  widget: WidgetType.FUTURE_DATE_TIME,
});

type CountdownTimerWidgetOptions = {
  name?: string;
  label?: string;
};

export const countdownTimerWidget = ({
  name = 'countdownTimer',
  label = 'Select Timer Date',
}: CountdownTimerWidgetOptions) => ({
  name: name || 'countdownTimer',
  label: label || 'Select Timer Date',
  widget: WidgetType.FUTURE_DATE_TIME,
});

type CloudinaryVideoWidgetOptions = {
  name?: string;
  label?: string;
  required?: boolean;
};

export const cloudinaryVideoWidget = ({
  name,
  label,
  required = false,
}: CloudinaryVideoWidgetOptions) => ({
  name: name || 'video',
  label: label || 'Video',
  widget: WidgetType.FILE,
  required,
});

type SelectWidgetConfig = {
  name: string;
  label: string;
  options: string[] | SelectWidgetOption[];
  multiple?: boolean;
  required?: boolean;
  defaultValue?: string;
  hint?: string;
  i18n?: boolean;
};

export const selectWidget = ({
  name,
  label,
  options,
  multiple = false,
  required = false,
  defaultValue = '',
  hint = '',
  i18n = false,
}: SelectWidgetConfig): WidgetBaseInterface => ({
  widget: WidgetType.SELECT,
  name,
  label,
  options,
  multiple,
  required,
  default: defaultValue,
  hint,
  i18n,
});

type ListWidgetWithFieldOptions = {
  name: string;
  label: string;
  field: string | WidgetBaseInterface;
  hint?: string;
  summary?: string;
  i18n?: boolean;
  min?: number;
};

export const listWidgetWithField = ({
  name,
  label,
  field,
  hint = '',
  summary,
  min,
  i18n = false,
}: ListWidgetWithFieldOptions): WidgetBaseInterface => ({
  name,
  label,
  widget: WidgetType.LIST,
  field,
  hint,
  summary,
  default: [] as unknown[],
  collapsed: true,
  min,
  i18n,
});

type ListWidgetWithFieldsOptions = {
  name: string;
  label: string;
  fields: WidgetBaseInterface[];
  hint?: string;
  defaultList?: unknown[];
  summary?: string;
  i18n?: boolean;
};

export const listWidgetWithFields = ({
  name,
  label,
  fields,
  hint = '',
  defaultList = [],
  summary,
  i18n = false,
}: ListWidgetWithFieldsOptions): WidgetBaseInterface => ({
  name,
  label,
  widget: WidgetType.LIST,
  fields,
  hint,
  summary,
  default: defaultList,
  collapsed: true,
  i18n,
});
